import { KeyboardEventHandler, RefObject, createRef, useState } from 'react';

type Direction = 'horizontal' | 'vertical';

type Props = {
  initialIndex?: number;
  selector?: string;
  direction?: Direction;
};

type Return = [RefObject<HTMLDivElement>, KeyboardEventHandler];

const KEYS: Record<Direction, string[]> = {
  horizontal: ['ArrowLeft', 'ArrowRight'],
  vertical: ['ArrowUp', 'ArrowDown'],
};

/**
 * Hook to implement keyboard navigation when all other options fail but the show must go on.
 *
 * Requires proper DOM configuration to function properly:
 * - each tab group is wrapped in a container that receives the `boxRef` and `onKeyDown` outputs of the hook
 * - the item at `initialIndex` in the tab group must have `tabIndex = 0`
 * - every other item in the tab group must have `tabIndex = -1`
 *
 * @example
 * // Pressing Tab will navigate across rows
 * // Once the focus is in a row, the item with `tabIndex = 0` is focused.
 * // To move across items in the tab group, arrow keys should be used instead.
 * // In the example, navigation works as follows:
 * // - `Tab` will focus "Cell 0 0"
 * // - `ArrowRight` will focus "Cell 0 1"
 * // - `ArrowLeft` will focus "Cell 0 0"
 * // - `Tab` will focus "Cell 1 1"
 * // - `Shift + Tab` will focus "Cell 0 0"
 *
 * const [ref1, onKeyDown1] = useTabbable();
 * const [ref2, onKeyDown2] = useTabbable({ initialIndex: 1, selector: '[data-role="something-else"]' });
 *
 * <div className="container">
 *  <div className="row" ref={ref1} onKeyDown={onKeyDown1}>
 *    <div tabIndex={0} className="column" data-role="tabbable">Cell 0 0</div>
 *    <div tabIndex={-1} className="column" data-role="tabbable">Cell 0 1</div>
 *  </div>
 *  <div className="row" ref={ref2} onKeyDown={onKeyDown2}>
 *    <div tabIndex={-1} className="column" data-role="something-else">Cell 1 0</div>
 *    <div tabIndex={0} className="column" data-role="something-else">Cell 1 1</div>
 *  </div>
 * </div>
 *
 * @param {Props} props - hook customization properties
 * @param {number} [props.initialIndex=0] - index of the tabbable item that should be the first in the tab sequence
 * @param {string} [props.selector="[data-role='tabbable']"] - selector for tabbable elements
 * @param {string} [props.direction="horizontal"] - whether navigation should occur on ArrowLeft/ArrowRight or ArrowUp/ArrowDown
 */
export const useTabbable = ({ initialIndex = 0, selector = '[data-role="tabbable"]', direction = 'horizontal' }: Props = {}): Return => {
  const [currentActive, setCurrentActive] = useState(initialIndex);
  const boxRef = createRef<HTMLDivElement>();

  const onKeyDown: KeyboardEventHandler = (e) => {
    const [arrowPrevious, arrowNext] = KEYS[direction];
    if (![arrowPrevious, arrowNext, 'Tab'].includes(e.code) || !boxRef.current) {
      return;
    }
    if (e.code === 'Tab') {
      setCurrentActive(0);
      return;
    }

    // Event is a navigation among tabbable elements, prevent the browser from scrolling on arrow clicks
    e.preventDefault();

    const tabs = boxRef.current.querySelectorAll(selector) as NodeListOf<HTMLElement>;
    let newActive = currentActive;
    if (e.code === arrowNext) {
      newActive = (currentActive + 1) % tabs.length;
    } else {
      newActive = currentActive === 0 ? tabs.length - 1 : currentActive - 1;
    }
    tabs[newActive].focus();
    setCurrentActive(newActive);
  };

  return [boxRef, onKeyDown];
};
